import { useMemo } from 'react'
import { Cell, RadialBar, RadialBarChart } from 'recharts'

import { ExportButtonV2 } from '@/catalog-apps/components/component-library/DataTableV2/components/ExportButtonV2'
import { DataTableV2DataRow } from '@/catalog-apps/components/component-library/DataTableV2/types'
import { ComponentActionsContainer } from '@/catalog-apps/components/component-library/shared/ComponentActionsContainer'
import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import { normalizeRechartsData } from '@/catalog-apps/components/component-library/shared/rechartsDataUtils'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/shadcn/charts'

import { AiRadialBarChartDataItem, AiRadialBarChartSchema } from './types-ai-radial-bar-chart'

/** Matches `--chart-1` ... `--chart-5` on `:root` and `.dark` in `index.scss`. */
const THEME_CHART_COLOR_SLOT_COUNT = 5

const DEFAULT_MIN_CHART_HEIGHT = 320

type AiRadialBarChartDataItemWithFill = AiRadialBarChartDataItem & { fill: string }

function themeChartFillHsl(categoryIndex: number): string {
  const slot = (categoryIndex % THEME_CHART_COLOR_SLOT_COUNT) + 1
  return `hsl(var(--chart-${slot}))`
}

export function AiRadialBarChart({ schema }: { schema: AiRadialBarChartSchema }) {
  const exportData = useMemo<DataTableV2DataRow[]>(
    () =>
      schema.data.map((item, index) => {
        const category = item[schema.config.categoryKey]
        const categoryKey = typeof category === 'string' ? category : String(category ?? index + 1)
        const categoryLabel = schema.config.categoryConfig[categoryKey]?.label ?? category

        return {
          id: index + 1,
          [schema.config.categoryKey]: categoryLabel,
          [schema.config.valueKey]: item[schema.config.valueKey],
        }
      }),
    [schema]
  )

  const { data: normalizedData, mapDataKey } = normalizeRechartsData(schema.data, [
    schema.config.categoryKey,
    schema.config.valueKey,
  ])
  const normalizedCategoryKey = mapDataKey(schema.config.categoryKey)
  const normalizedValueKey = mapDataKey(schema.config.valueKey)
  const chartData = useMemo<AiRadialBarChartDataItemWithFill[]>(
    () =>
      normalizedData.map((item, index) => {
        const category = item[normalizedCategoryKey]
        const categoryKey = typeof category === 'string' ? category : String(category ?? index + 1)
        const categoryConfig = schema.config.categoryConfig[categoryKey]

        return {
          ...item,
          fill: categoryConfig?.color ?? themeChartFillHsl(index),
        }
      }),
    [normalizedCategoryKey, normalizedData, schema.config.categoryConfig]
  )

  return (
    <div className="flex h-[50vh] w-full flex-col overflow-hidden">
      <ComponentHeader emphasize wrapperClassName="rounded-b-none">
        <ComponentTitle title={schema.title} description={schema.description} />
        <ComponentActionsContainer className="bg-transparent p-0">
          <ExportButtonV2
            data={exportData}
            title={schema.title}
            selectionRequired={false}
            className="text-background dark:text-foreground"
          />
        </ComponentActionsContainer>
      </ComponentHeader>
      <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-background">
        <ChartContainer
          className="mx-auto aspect-auto h-full min-h-0 w-full"
          config={schema.config.categoryConfig}
          style={{ minHeight: schema.config.minChartHeight ?? DEFAULT_MIN_CHART_HEIGHT }}
        >
          <RadialBarChart
            accessibilityLayer
            data={chartData}
            layout={schema.config.layout ?? 'radial'}
            startAngle={schema.config.startAngle ?? 90}
            endAngle={schema.config.endAngle ?? -270}
            innerRadius={schema.config.innerRadius ?? '25%'}
            outerRadius={schema.config.outerRadius ?? '90%'}
            cx={schema.config.cx ?? '50%'}
            cy={schema.config.cy ?? '50%'}
            barSize={schema.config.barSize}
            maxBarSize={schema.config.maxBarSize}
            barCategoryGap={schema.config.barCategoryGap ?? '10%'}
            barGap={schema.config.barGap ?? 4}
            margin={{ top: 12, right: 12, bottom: 12, left: 12 }}
          >
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="dot" nameKey={normalizedCategoryKey} />}
            />
            {schema.config.showLegend && (
              <ChartLegend content={<ChartLegendContent nameKey={normalizedCategoryKey} />} />
            )}
            <RadialBar dataKey={normalizedValueKey} background>
              {chartData.map((item, index) => (
                <Cell key={`${item[normalizedCategoryKey]}-${index}`} fill={String(item.fill)} />
              ))}
            </RadialBar>
          </RadialBarChart>
        </ChartContainer>
      </div>
    </div>
  )
}
